import { defineRecipe } from "@chakra-ui/react";
import type { ThemeComponentOverrides } from "../types/theme-overrides";

type ButtonSizeOverrides = NonNullable<
  NonNullable<ThemeComponentOverrides["button"]>["sizes"]
>[keyof NonNullable<NonNullable<ThemeComponentOverrides["button"]>["sizes"]>];

const DEFAULT_SIZE_FONT: Record<string, string> = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
};

function shouldResetIntrinsicSizing(
  overrides: ThemeComponentOverrides["button"] | undefined,
  sizeOverrides?: ButtonSizeOverrides
): boolean {
  return Boolean(
    overrides?.paddingX ||
      overrides?.paddingY ||
      sizeOverrides?.paddingX ||
      sizeOverrides?.paddingY
  );
}

function buildSizeVariant(
  overrides: ThemeComponentOverrides["button"] | undefined,
  sizeName: string,
  sizeOverrides?: ButtonSizeOverrides
): {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  letterSpacing?: string;
  textTransform?: string;
  px?: string;
  py?: string;
  borderRadius?: string;
  boxShadow?: string;
  h?: string;
  minW?: string;
} {
  const shouldResetSizing = shouldResetIntrinsicSizing(
    overrides,
    sizeOverrides
  );

  return {
    ...(shouldResetSizing ? { h: "auto", minW: "auto" } : {}),
    fontSize: sizeOverrides?.fontSize ?? DEFAULT_SIZE_FONT[sizeName],
    fontFamily: sizeOverrides?.fontFamily,
    fontWeight: sizeOverrides?.fontWeight,
    letterSpacing: sizeOverrides?.letterSpacing,
    textTransform: sizeOverrides?.textTransform,
    px: sizeOverrides?.paddingX,
    py: sizeOverrides?.paddingY,
    borderRadius: sizeOverrides?.borderRadius,
    boxShadow: sizeOverrides?.shadow,
  };
}

export function buildButtonRecipe(
  overrides?: ThemeComponentOverrides["button"]
): ReturnType<typeof defineRecipe> {
  const sizeVariants = {
    xs: buildSizeVariant(overrides, "xs", overrides?.sizes?.xs),
    sm: buildSizeVariant(overrides, "sm", overrides?.sizes?.sm),
    md: buildSizeVariant(overrides, "md", overrides?.sizes?.md),
    lg: buildSizeVariant(overrides, "lg", overrides?.sizes?.lg),
  } as const;

  return defineRecipe({
    base: {
      borderRadius: overrides?.borderRadius,
      fontFamily: overrides?.fontFamily,
      fontSize: overrides?.fontSize,
      fontWeight: overrides?.fontWeight ?? "700",
      letterSpacing: overrides?.letterSpacing,
      textTransform: overrides?.textTransform,
      px: overrides?.paddingX,
      py: overrides?.paddingY,
      boxShadow: overrides?.shadow,
    },
    variants: {
      size: sizeVariants,
    },
    defaultVariants: overrides?.defaultSize
      ? ({ size: overrides.defaultSize } as Record<string, string>)
      : undefined,
  });
}
